import cn from 'classnames'
import { useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Input } from 'core-components/library/inputs'
import {
  getAttentionSeverityColor,
  type IAggregatedAttentionStatus,
  type TAttentionStatusSeverity,
} from 'partner360/entities/attentionStatuses'

import type { ICockpitReminderPartner } from '../../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IAttentionPartnerOverview } from '../../../../lib'
import { isSameEmail } from '../../../../lib'
import type { IAttentionPartnerOwners } from '../../../../model/selectors/selectCockpitAttentionLevel'
import { useCanReplaceAttentionPbm } from '../../hooks/useAttentionPbmReplacement'
import { PARTNER_UNIT, useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import { AttentionCount } from '../attentionCount'
import { AttentionPartnerLink } from '../attentionPartnerLink'
import {
  AttentionPbmReplacePopup,
  type IAttentionPbmPickerPartner,
} from '../attentionPbmReplacement'
import { AttentionReminderButton } from '../attentionReminderButton'

import { AttentionFullscreenPartnerPanel } from './AttentionFullscreenPartnerPanel'
import { AttentionFullscreenPartnerWorkspace } from './AttentionFullscreenPartnerWorkspace'

import styles from './AttentionStatusesFullscreen.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

/** Owners the second panel names; the rest are one pick away in the navigator. */
const TOP_OWNERS = 5

export const AttentionSeverityPill = ({ severity }: { severity: TAttentionStatusSeverity }) => {
  const text = useAttentionStatusesText()

  return (
    <span className={styles.pill} style={{ background: getAttentionSeverityColor(severity) }}>
      <Translation span {...text.phrase.severityName(severity)} />
    </span>
  )
}

interface IAttentionFullscreenOverviewProps {
  /** The PBM or manager the level stands for, who can be reminded from its header. */
  owner?: { email: string; name: string; kind: 'pbm' | 'manager' }
  /** What the level is: a name the payload owns, e.g. `Germany`, or a heading of ours. */
  scope: { label?: string; labelKey?: string }
  /** Levels above this one, outermost first; the first stands for everything in view. */
  crumbs: { key: string | null; label?: string; labelKey?: string }[]
  onNavigate: (key: string | null) => void
  /** Every partner the level holds, open or not. */
  partnerIds: string[]
  statuses: IAggregatedAttentionStatus[]
  overviews: Map<string, IAttentionPartnerOverview>
  ownersByPartner: Map<string, IAttentionPartnerOwners>
  selectedBit: number | null
  onSelectStatus: (bitValue: number | null) => void
  /** Whether the owners panel is drawn; a PBM has no one else to look at. */
  showOwners: boolean
  /** A picked partner opens to be worked on — its statuses and their actions — rather than beside
   *  the table. The PBM's view: the partners are their own. */
  opensPartnerWorkspace?: boolean
  /** The partner the workspace opens on, and the offering of the status to show first. */
  initialDetail?: { partnerId: string; offering: string | null } | null
}

/**
 * One level of the full-screen view: what is open there by status, who holds the most of it, and
 * its partners — each with how many statuses it holds open and the most urgent of them.
 */
export const AttentionFullscreenOverview = ({
  owner,
  scope,
  crumbs,
  onNavigate,
  partnerIds,
  statuses,
  overviews,
  ownersByPartner,
  selectedBit,
  onSelectStatus,
  showOwners,
  opensPartnerWorkspace = false,
  initialDetail = null,
}: IAttentionFullscreenOverviewProps) => {
  const t = useSafetyTranslation()
  const text = useAttentionStatusesText()
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(initialDetail?.partnerId ?? null)
  const query = search.trim().toLowerCase()
  /* The level's name as a phrase's part; the header renders it on its own. */
  const scopeLabel = scope.label ?? t(scope.labelKey ?? '')

  const levelIds = useMemo(() => new Set(partnerIds), [partnerIds])

  const byStatus = useMemo(
    () =>
      statuses
        .map((status) => ({
          status,
          count: status.partnerIds.filter((partnerId) => levelIds.has(partnerId)).length,
        }))
        .filter(({ count }) => count > 0)
        .sort(
          (left, right) => right.status.severity - left.status.severity || right.count - left.count
        ),
    [levelIds, statuses]
  )
  const maxCount = Math.max(1, ...byStatus.map(({ count }) => count))

  const rows = useMemo(
    () =>
      partnerIds
        .map((partnerId) => overviews.get(partnerId))
        .filter((overview): overview is IAttentionPartnerOverview => Boolean(overview))
        .filter(
          ({ partner, open }) =>
            open.length > 0 &&
            (selectedBit === null || open.some(({ bitValue }) => bitValue === selectedBit)) &&
            (!query || partner.partnerName.toLowerCase().includes(query))
        )
        .sort(
          (left, right) =>
            right.worstSeverity - left.worstSeverity || right.open.length - left.open.length
        ),
    [overviews, partnerIds, query, selectedBit]
  )

  /* What a reminder from this view is about: the picked status, or every open status without one.
     The search narrows what is read, not who is reminded. */
  const remindPartners = useMemo<ICockpitReminderPartner[]>(
    () =>
      partnerIds.flatMap((partnerId) => {
        const open = overviews.get(partnerId)?.open ?? []
        const isRaised =
          open.length > 0 &&
          (selectedBit === null || open.some(({ bitValue }) => bitValue === selectedBit))

        return isRaised ? [{ partnerId, statuses: open.map(({ bitValue }) => bitValue) }] : []
      }),
    [overviews, partnerIds, selectedBit]
  )

  const topOwners = useMemo(() => {
    const byOwner = new Map<
      string,
      {
        email: string
        name: string
        partners: number
        open: number
        remindPartners: ICockpitReminderPartner[]
      }
    >()

    rows.forEach(({ partner, open }) =>
      (ownersByPartner.get(partner.partnerId)?.pbms ?? []).forEach(({ ownerId, ownerName }) => {
        const entry = byOwner.get(ownerId) ?? {
          email: ownerId,
          name: ownerName,
          partners: 0,
          open: 0,
          remindPartners: [],
        }

        entry.partners += 1
        entry.open += open.length
        entry.remindPartners.push({
          partnerId: partner.partnerId,
          statuses: open.map(({ bitValue }) => bitValue),
        })
        byOwner.set(ownerId, entry)
      })
    )

    return [...byOwner.values()].sort((left, right) => right.open - left.open).slice(0, TOP_OWNERS)
  }, [ownersByPartner, rows])

  const canReplace = useCanReplaceAttentionPbm()

  /* Every partner of the level a PBM holds, open or not: the ones a replacement can hand over. */
  const getPbmPartners = (email: string): IAttentionPbmPickerPartner[] =>
    partnerIds
      .filter((partnerId) =>
        (ownersByPartner.get(partnerId)?.pbms ?? []).some(({ ownerId }) =>
          isSameEmail(ownerId, email)
        )
      )
      .map((partnerId) => ({
        partnerId,
        partnerName: overviews.get(partnerId)?.partner.partnerName ?? partnerId,
      }))

  const ownerPartners = owner ? getPbmPartners(owner.email) : []

  /* The panel follows the row only while the row is still listed. The partner being worked on
     stays open while the list beside it is searched. */
  const selectedOverview = opensPartnerWorkspace
    ? selectedId
      ? overviews.get(selectedId)
      : undefined
    : rows.find(({ partner }) => partner.partnerId === selectedId)

  const withOpen = partnerIds.filter((partnerId) => overviews.get(partnerId)?.open.length).length

  /* The partner being worked on takes the width; the list shrinks to the names beside it, so the
     next partner is still one click away. */
  if (opensPartnerWorkspace && selectedOverview) {
    return (
      <div className={styles.workspaceSplit}>
        <div className={styles.workspaceList}>
          <Input
            classes={{ root: styles.navSearch }}
            value={search}
            onChange={setSearch}
            startContent={<i className="kbase kbase-search" />}
            placeholder={t(KEYS.search.placeholderPartner)}
          />
          <ul className={styles.navList}>
            {rows.map(({ partner, open, worstSeverity }) => {
              const isSelected = partner.partnerId === selectedOverview.partner.partnerId

              return (
                <li
                  key={partner.partnerId}
                  className={cn(styles.navRow, { [styles.navRowSelected]: isSelected })}
                >
                  <button
                    type="button"
                    className={styles.navLabel}
                    aria-current={isSelected || undefined}
                    onClick={() => setSelectedId(partner.partnerId)}
                  >
                    <span className={styles.navPartner}>
                      <span className={styles.navName}>{partner.partnerName}</span>
                      <span className={styles.navSub}>{partner.country}</span>
                    </span>
                    <span
                      className={styles.navBadge}
                      style={{ background: getAttentionSeverityColor(worstSeverity) }}
                    >
                      {open.length}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
        <AttentionFullscreenPartnerWorkspace
          key={selectedOverview.partner.partnerId}
          overview={selectedOverview}
          preferredBit={selectedBit}
          preferredOffering={
            selectedOverview.partner.partnerId === initialDetail?.partnerId
              ? initialDetail.offering
              : null
          }
          onClose={() => setSelectedId(null)}
        />
      </div>
    )
  }

  return (
    <div className={styles.overview}>
      <div className={styles.toolbar}>
        {(scope.label ? crumbs : []).map((crumb) => (
          <span key={crumb.key ?? 'root'} className={styles.crumb}>
            <button
              type="button"
              className={styles.crumbLink}
              onClick={() => onNavigate(crumb.key)}
            >
              {crumb.label ?? <Translation span text={crumb.labelKey ?? ''} />}
            </button>
            <span aria-hidden="true">›</span>
          </span>
        ))}
        <span className={styles.scopeName}>
          {scope.label ?? <Translation span text={scope.labelKey ?? ''} />}
        </span>
        <span className={styles.scopeSub}>
          <Translation span {...text.phrase.partnersWithOpen(withOpen, partnerIds.length)} />
        </span>
        {owner && (
          <span className={cn(styles.ownerActions, styles.levelRemind)}>
            {canReplace && ownerPartners.length > 0 && (
              <AttentionPbmReplacePopup pbm={owner} partners={ownerPartners} />
            )}
            <AttentionReminderButton
              reminder={owner}
              route={{
                bitValue: selectedBit,
                actionId: owner.kind === 'manager' ? 'remindManager' : 'remindPbm',
              }}
              list={{ partners: remindPartners }}
              filled
            />
          </span>
        )}
        <Input
          classes={{ root: styles.search }}
          value={search}
          onChange={setSearch}
          startContent={<i className="kbase kbase-search" />}
          placeholder={t(KEYS.search.placeholderPartner)}
        />
      </div>

      <div className={cn(styles.panels, { [styles.panelsSingle]: !showOwners })}>
        <section className={styles.panel}>
          <h4 className={styles.panelTitle}>
            <Translation span {...text.phrase.openByStatus(scopeLabel)} />
          </h4>
          {byStatus.map(({ status, count }) => (
            <button
              key={status.bitValue}
              type="button"
              className={cn(styles.bar, { [styles.barSelected]: status.bitValue === selectedBit })}
              aria-pressed={status.bitValue === selectedBit}
              onClick={() =>
                onSelectStatus(status.bitValue === selectedBit ? null : status.bitValue)
              }
            >
              <span className={styles.barLabel}>{text.statusTitleParts(status, 1).text}</span>
              <span className={styles.barTrack}>
                <i
                  style={{
                    width: `${(count / maxCount) * 100}%`,
                    background: getAttentionSeverityColor(status.severity),
                  }}
                />
              </span>
              <span className={styles.barCount}>{count}</span>
            </button>
          ))}
        </section>

        {showOwners && (
          <section className={styles.panel}>
            <h4 className={styles.panelTitle}>
              <Translation span {...text.phrase.topPbms(scopeLabel)} />
            </h4>
            {topOwners.map((owner) => (
              <div key={owner.email} className={styles.owner}>
                <span className={styles.ownerName}>{owner.name}</span>
                <span className={styles.ownerSub}>
                  <AttentionCount count={owner.partners} unit={PARTNER_UNIT} />
                </span>
                <span className={styles.ownerOpen}>
                  <Translation span {...text.phrase.openCount(owner.open)} />
                </span>
                <span className={styles.ownerActions}>
                  {canReplace && (
                    <AttentionPbmReplacePopup
                      pbm={{ email: owner.email, name: owner.name }}
                      partners={getPbmPartners(owner.email)}
                    />
                  )}
                  <AttentionReminderButton
                    reminder={{ email: owner.email, name: owner.name }}
                    route={{ bitValue: selectedBit, actionId: 'remindPbm' }}
                    list={{ partners: owner.remindPartners }}
                    filled
                  />
                </span>
              </div>
            ))}
          </section>
        )}
      </div>

      {rows.length === 0 ? (
        <p className={styles.empty}>
          <Translation span text={KEYS.fullscreen.empty} />
        </p>
      ) : (
        <div className={styles.tableSplit}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <Translation span text={KEYS.fullscreen.columns.partner} />
                  </th>
                  <th>
                    <Translation span text={KEYS.fullscreen.columns.pbm} />
                  </th>
                  <th className={styles.numberCell}>
                    <Translation span text={KEYS.fullscreen.columns.open} />
                  </th>
                  <th>
                    <Translation span text={KEYS.fullscreen.columns.severity} />
                  </th>
                  <th className={styles.actionsCell} aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ partner, open, worstSeverity }) => {
                  const isSelected = selectedId === partner.partnerId
                  const pbms = ownersByPartner.get(partner.partnerId)?.pbms ?? []

                  return (
                    <PartnerRow
                      key={partner.partnerId}
                      name={partner.partnerName}
                      partnerId={partner.partnerId}
                      pbmNames={pbms.map(({ ownerName }) => ownerName).join(', ')}
                      openCount={open.length}
                      worstSeverity={worstSeverity}
                      isSelected={isSelected}
                      onSelect={() => setSelectedId(isSelected ? null : partner.partnerId)}
                    />
                  )
                })}
              </tbody>
            </table>
          </div>
          {selectedOverview && (
            <AttentionFullscreenPartnerPanel
              overview={selectedOverview}
              owners={ownersByPartner.get(selectedOverview.partner.partnerId)}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      )}
    </div>
  )
}

interface IPartnerRowProps {
  name: string
  partnerId: string
  pbmNames: string
  openCount: number
  worstSeverity: number
  isSelected: boolean
  onSelect: () => void
}

const PartnerRow = ({
  name,
  partnerId,
  pbmNames,
  openCount,
  worstSeverity,
  isSelected,
  onSelect,
}: IPartnerRowProps) => (
  <tr
    className={cn(styles.partnerRow, { [styles.partnerRowSelected]: isSelected })}
    tabIndex={0}
    aria-selected={isSelected}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onSelect()
      }
    }}
  >
    <td>{name}</td>
    <td>{pbmNames}</td>
    <td className={styles.numberCell}>{openCount}</td>
    <td>
      <AttentionSeverityPill severity={worstSeverity} />
    </td>
    <td className={styles.actionsCell}>
      <AttentionPartnerLink partnerId={partnerId} partnerName={name} className={styles.linkChip} />
    </td>
  </tr>
)
